import { useState, useEffect } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import MilesAvatar from './MilesAvatar';
import { safeGetItem, safeSetItem } from '../../shared/safeStorage';

interface MilesChatCtaProps {
  onClick: () => void;
}

const STORAGE_KEY = 'miles-cta-collapsed';

/**
 * Collapsible CTA button for MILES chat that appears in the bottom-right corner.
 * Can be minimized to a small tab when users need more screen space.
 * Enterprise-grade design with subtle, professional animations.
 */
function MilesChatCta({ onClick }: MilesChatCtaProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem(STORAGE_KEY) === 'true';
    }
    return false;
  });

  useEffect(() => {
    safeSetItem(STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCollapsed((prev) => !prev);
  };

  // Collapsed state - minimal tab on the right edge
  if (isCollapsed) {
    return (
      <div className="fixed bottom-6 right-0 z-50 flex items-center">
        {/* Expand button */}
        <button
          onClick={handleToggleCollapse}
          className="group flex h-10 w-5 items-center justify-center rounded-l-md bg-primary/90 text-white/70 shadow-md transition-all duration-200 hover:w-6 hover:bg-primary hover:text-white"
          aria-label="Expand MILES Assistant"
        >
          <CaretLeft
            size={14}
            weight="bold"
            className="transition-transform duration-200 group-hover:scale-110"
          />
        </button>
      </div>
    );
  }

  // Expanded state - full CTA
  return (
    <div className="fixed bottom-6 right-1 z-50 flex items-center gap-0.5">
      {/* Collapse button */}
      <button
        onClick={handleToggleCollapse}
        className="group flex h-8 w-5 items-center justify-center rounded-l-md bg-primary/80 text-white/60 shadow-sm transition-all duration-200 hover:bg-primary hover:text-white"
        aria-label="Minimize MILES Assistant"
      >
        <CaretRight
          size={12}
          weight="bold"
          className="transition-transform duration-200 group-hover:scale-110"
        />
      </button>

      {/* Main CTA button */}
      <button
        onClick={onClick}
        className="group relative flex items-center gap-3 overflow-hidden rounded-lg bg-primary px-2 py-1 text-white shadow-[0_4px_20px_-2px_rgba(29,33,59,0.35),0_2px_8px_-2px_rgba(29,33,59,0.2)] transition-all duration-200 ease-out hover:shadow-[0_8px_30px_-2px_rgba(29,33,59,0.45),0_4px_12px_-2px_rgba(29,33,59,0.25)] active:scale-[0.98]"
        aria-label="Open MILES AI Assistant"
      >
        {/* Subtle shine sweep on hover */}
        <div className="absolute inset-0 -translate-x-full skew-x-[-12deg] bg-gradient-to-r from-transparent via-white/[0.08] to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />

        <div className="relative z-10 flex flex-col items-start">
          <span className="text-[12px] font-semibold leading-tight tracking-tight">
            MILES
          </span>
          <span className="text-[10px] font-medium uppercase tracking-wider text-white/60">
            AI Assistant
          </span>
          <MilesAvatar size="md" loop={true} className="rounded-lg" />
        </div>
      </button>
    </div>
  );
}

export default MilesChatCta;
